import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  ExecutionDisciplineCard,
  type ExecutionDisciplineView,
} from '../components/domain/ExecutionDisciplineCard';
import { TradeReplayChart } from '../components/domain/TradeReplayChart';
import { useAccounts } from '../features/accounts/queries';
import { useLinkExitFill } from '../features/positions/mutations';
import {
  usePositionDetail,
  usePositionLifecycle,
  usePositionReplay,
} from '../features/positions/queries';
import type { PositionLifecycleEventDto } from '../lib/api/endpoints';
import { formatCurrency } from '../lib/format/currency';
import { cn } from '../lib/utils';

function eventTone(event: PositionLifecycleEventDto['event']): string {
  if (event === 'BUY') return 'border-success/60 bg-success/10';
  if (event === 'SELL') return 'border-danger/60 bg-danger/10';
  if (event === 'STOP_UPDATE') return 'border-warn/60 bg-warn-subtle';
  return 'border-border bg-surface';
}

function eventTitle(event: PositionLifecycleEventDto): string {
  if (event.event === 'STOP_UPDATE') return 'Stop updated';
  if (event.event === 'PLAN') return 'Plan submitted';
  return event.event;
}

export function PositionLifecycle() {
  const { symbol = '' } = useParams();
  const [searchParams] = useSearchParams();
  const accounts = useAccounts();
  const accountId = searchParams.get('accountId') ?? accounts.data?.[0]?.id ?? null;
  const account = accounts.data?.find((item) => item.id === accountId) ?? accounts.data?.[0];
  const currency = account?.currency ?? 'USD';
  const [replayScope, setReplayScope] = useState<'current' | 'full' | undefined>();
  const position = usePositionDetail(accountId, symbol);
  const lifecycle = usePositionLifecycle(accountId, symbol);
  const replay = usePositionReplay(accountId, symbol, replayScope);
  const linkExitFill = useLinkExitFill(accountId, symbol);
  const data = lifecycle.data;
  const discipline = (
    position.data as typeof position.data & { discipline?: ExecutionDisciplineView }
  )?.discipline;

  useEffect(() => {
    setReplayScope(undefined);
  }, [accountId, symbol]);

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <nav className="text-sm text-text-muted" aria-label="Breadcrumb">
          <Link className="text-primary hover:text-primary-hover" to="/positions">
            Positions
          </Link>
          <span> / {symbol.toUpperCase()} lifecycle</span>
        </nav>
        {position.isLoading ? (
          <p className="text-sm text-text-muted">Loading discipline state...</p>
        ) : position.isError ? (
          <p className="text-sm text-danger">Could not load discipline state.</p>
        ) : discipline ? (
          <ExecutionDisciplineCard
            discipline={discipline}
            linkingTradeId={linkExitFill.isPending ? linkExitFill.variables?.tradeId : null}
            onLinkExitFill={(input) => linkExitFill.mutate(input)}
          />
        ) : null}
        {linkExitFill.isError ? (
          <p className="text-sm text-danger">
            Could not link this exit fill. The fill must be unlinked and belong to this position.
          </p>
        ) : null}
        {replay.isLoading ? (
          <p className="text-sm text-text-muted">Loading trade replay...</p>
        ) : replay.isError ? (
          <p className="text-sm text-danger">Could not load trade replay.</p>
        ) : replay.data ? (
          <TradeReplayChart
            currency={currency}
            replay={replay.data}
            onScopeChange={setReplayScope}
          />
        ) : null}
        {lifecycle.isLoading ? (
          <p className="text-sm text-text-muted">Loading lifecycle...</p>
        ) : lifecycle.isError || !data ? (
          <p className="text-sm text-danger">Could not load lifecycle.</p>
        ) : (
          <>
            <header className="flex flex-col gap-4 border-b border-border pb-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h1 className="text-2xl font-semibold">{data.symbol}</h1>
                <p className="mt-1 text-sm text-text-muted">
                  {data.status} | first entry {data.firstBuyDate} | {data.daysHeld} days held
                </p>
              </div>
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <Metric label="Open Qty" value={data.currentQty.toString()} />
                <Metric label="Avg Cost" value={formatCurrency(data.avgCost, currency)} />
                <Metric label="Current" value={formatCurrency(data.currentPrice, currency)} />
              </div>
            </header>

            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <Metric label="Bought" value={data.totalSharesEverBought.toString()} />
              <Metric label="Sold" value={data.totalSharesEverSold.toString()} />
              <Metric label="Realized P&L" value={formatCurrency(data.realisedPnl, currency)} />
              <Metric label="Unrealized P&L" value={formatCurrency(data.unrealisedPnl, currency)} />
              <Metric label="Total P&L" value={formatCurrency(data.totalPnl, currency)} />
            </section>

            {data.stopHistory.length > 0 ? (
              <section className="rounded border border-border bg-surface p-4">
                <h2 className="text-lg font-semibold">Stop History</h2>
                <div className="mt-3 space-y-2 text-sm">
                  {data.stopHistory.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <span className="tabular-nums">
                        {item.isInitial
                          ? `Stop set: ${formatCurrency(item.newStop, currency)} on unknown date`
                          : `${item.oldStop ? formatCurrency(item.oldStop, currency) : '-'} -> ${formatCurrency(item.newStop, currency)}`}
                      </span>
                      <span className="text-text-muted">
                        {item.changedAt ? item.changedAt.slice(0, 10) : 'unknown'}
                        {item.reason ? ` | ${item.reason}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="rounded border border-border bg-surface p-4">
              <h2 className="text-lg font-semibold">Timeline</h2>
              <div className="mt-4 space-y-3">
                {data.timeline.map((event, index) => (
                  <article
                    key={`${event.event}-${event.date}-${event.tradeId ?? event.planId ?? index}`}
                    className={cn('rounded border p-3', eventTone(event.event))}
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="font-semibold">{eventTitle(event)}</h3>
                        <p className="text-sm text-text-muted">{event.date}</p>
                      </div>
                      {event.runningQty !== null ? (
                        <span className="text-sm tabular-nums">Open after: {event.runningQty}</span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm tabular-nums">
                      {event.event === 'BUY' || event.event === 'SELL'
                        ? `${event.qty ?? 0} @ ${formatCurrency(event.price ?? 0, currency)}`
                        : null}
                      {event.event === 'SELL'
                        ? ` | P&L ${formatCurrency(event.pnlFromExit ?? 0, currency)} | ${(
                            event.pctClosed ?? 0
                          ).toFixed(1)}% closed`
                        : null}
                      {event.event === 'STOP_UPDATE'
                        ? `${event.oldStop ? formatCurrency(event.oldStop, currency) : '-'} -> ${event.newStop ? formatCurrency(event.newStop, currency) : '-'}`
                        : null}
                      {event.event === 'PLAN'
                        ? `${event.rulesResult ?? 'No decision'} | ${event.wasFollowed ? 'followed' : 'not linked'}`
                        : null}
                    </p>
                    {event.reason ? (
                      <p className="mt-1 text-sm text-text-muted">{event.reason}</p>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-surface p-3">
      <dt className="text-xs uppercase tracking-normal text-text-muted">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
